import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CustomBreakpointService } from '@core/services/breakpoint.service';
import { LinkService } from '@core/services/link.service';
import { UtmParametersService } from '@core/services/utm-parameters.service';
import { TButtonSize } from '@core/types/button';
import { UntilDestroy } from '@ngneat/until-destroy';
import { map } from 'rxjs';

import { ButtonComponentImports } from './button.imports';
import { EnButtonBreakpoint, IButtonConfig, IButtonIcon, ISideIcon } from './button.interface';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'noda-button-v2',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  imports: ButtonComponentImports,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonV2Component {
  protected readonly breakpoint$: CustomBreakpointService = inject(CustomBreakpointService);
  private utmService: UtmParametersService = inject(UtmParametersService);
  private linkService: LinkService = inject(LinkService);

  public btnSize: InputSignal<TButtonSize> = input<TButtonSize>('m');
  public isResizable: InputSignal<boolean> = input<boolean>(true);

  public appearance: InputSignal<string> = input<string>();
  public sideIcon: InputSignal<ISideIcon> = input<ISideIcon>();
  public icon: InputSignal<IButtonIcon> = input<IButtonIcon>();
  public text: InputSignal<string> = input.required<string>();
  public disabled: InputSignal<boolean> = input<boolean>();
  public staticLinkUrl: InputSignal<string> = input<string>();
  public staticQuerySaved: InputSignal<boolean> = input<boolean>(false);
  public link: InputSignal<string> = input<string>();
  public targetMethod: InputSignal<string> = input<string>('_self');
  public rel: InputSignal<string> = input<string>(null);
  public onButtonClick: OutputEmitterRef<void> = output();

  public sizeConfig: InputSignal<IButtonConfig> = input<IButtonConfig>({
    breakpoint: [EnButtonBreakpoint.desktopMedium, EnButtonBreakpoint.desktopLarge],
    reSize: 'l',
  });

  private match: Signal<boolean> = toSignal(
    this.breakpoint$.pipe(
      map(value => {
        return this.sizeConfig().breakpoint.includes(value as EnButtonBreakpoint);
      }),
    ),
  );

  public computedButtonSize: Signal<TButtonSize> = computed(() => {
    return this.match() ? this.sizeConfig().reSize : this.btnSize();
  });

  public buttonLink: Signal<string> = computed(() => {
    if (!this.staticLinkUrl()) return '';

    const link: string = this.staticQuerySaved()
      ? this.linkService.addQueryParams(this.staticLinkUrl())
      : this.staticLinkUrl();

    return this.utmService.addUtmParamsToUrl(link);
  });
}
